/**
 * Privacy — domain constants shared by the Record of Processing Activities (GDPR Article 30)
 * and, from phase 2, the DPIA register.
 *
 * The GDPR vocabularies that already existed in the AI Use Case Register are **re-exported
 * here, not copied**. Personal data must have one vocabulary across the product: a second
 * list of lawful bases or data categories would fork the domain model and make data
 * unmovable between the two registers. If a value needs changing it changes in
 * `aiGovernance.ts` and both registers follow.
 *
 * Mirrored server-side in `lambda/prod-nimbus-processing-activities/lambda_function.py`.
 * The closed vocabularies below are validated there too — the backend is the authority.
 */

import type {
  ActivityStatus,
  DataCategory,
  ProcessorRole,
  TransferMechanism,
  DpiaVerdict,
} from '../types/models';

// ─── Re-exported, single source of truth in aiGovernance.ts ───────────────────
export {
  LEGAL_BASIS_OPTIONS,
  DATA_CATEGORY_LABELS,
  DATA_CATEGORIES,
  SENSITIVITY_LABELS,
  SENSITIVITY_LEVELS,
  SENSITIVITY_TONE,
  DATA_SENSITIVITY_LABELS,
  DATA_SENSITIVITY_ANSWERS,
  DATA_SENSITIVITY_TONE,
  DATA_SENSITIVITY_HINT,
  dataFieldRiskCell,
} from './aiGovernance';

// ─── Controller / processor role ──────────────────────────────────────────────
// Article 30(1) applies to controllers, 30(2) to processors. Rather than two registers,
// the role distinguishes them — which is also what the DPIA template asks at §2.1.1
// ("data controller — alone or shared?") and §3.3.2.

export const PROCESSOR_ROLES: ProcessorRole[] = [
  'controller', 'joint-controller', 'processor', 'sub-processor',
];

export const PROCESSOR_ROLE_LABELS: Record<ProcessorRole, string> = {
  'controller':       'Controller',
  'joint-controller': 'Joint controller',
  'processor':        'Processor',
  'sub-processor':    'Sub-processor',
};

export const PROCESSOR_ROLE_DESCRIPTIONS: Record<ProcessorRole, string> = {
  'controller':       'We decide why and how this personal data is processed.',
  'joint-controller': 'We decide why and how, jointly with another organisation. Name them below.',
  'processor':        'We process this data on another organisation’s instructions, not our own.',
  'sub-processor':    'We process on behalf of another processor, further down the chain.',
};

export const PROCESSOR_ROLE_TONE: Record<ProcessorRole, string> = {
  'controller':       'primary',
  'joint-controller': 'info',
  'processor':        'neutral',
  'sub-processor':    'neutral',
};

// ─── Status ───────────────────────────────────────────────────────────────────
// Note there is no read-only lock on `active`: unlike a DPIA, a ROPA row is a living
// record. Article 30 requires it to be *current*, not frozen.

export const ACTIVITY_STATUSES: ActivityStatus[] = ['draft', 'active', 'retired'];

export const ACTIVITY_STATUS_LABELS: Record<ActivityStatus, string> = {
  draft:   'Draft',
  active:  'Active',
  retired: 'Retired',
};

export const ACTIVITY_STATUS_TONE: Record<ActivityStatus, string> = {
  draft:   'neutral',
  active:  'success',
  retired: 'dark',
};

export const ACTIVITY_STATUS_TRANSITIONS: Record<ActivityStatus, ActivityStatus[]> = {
  draft:   ['active'],
  active:  ['retired', 'draft'],
  retired: [],
};

export const ACTIVITY_ACTION_LABELS: Record<ActivityStatus, string> = {
  active:  'Activate',
  retired: 'Retire',
  draft:   'Return to Draft',
};

// ─── Data subjects — Article 30(1)(c) ─────────────────────────────────────────
// Nothing in the platform modelled data subjects before this register.

export const DATA_SUBJECT_CATEGORIES = [
  'customers', 'prospects', 'employees', 'job-applicants', 'former-employees',
  'contractors', 'supplier-contacts', 'website-visitors', 'members',
  'children', 'patients', 'students', 'public', 'other',
] as const;

export const DATA_SUBJECT_CATEGORY_LABELS: Record<string, string> = {
  'customers':         'Customers',
  'prospects':         'Prospects',
  'employees':         'Employees',
  'job-applicants':    'Job applicants',
  'former-employees':  'Former employees',
  'contractors':       'Contractors',
  'supplier-contacts': 'Supplier contacts',
  'website-visitors':  'Website visitors',
  'members':           'Members',
  'children':          'Children',
  'patients':          'Patients',
  'students':          'Students',
  'public':            'General public',
  'other':             'Other',
};

/**
 * Categories the GDPR and the EDPB treat as vulnerable, where the imbalance of power means
 * consent is rarely freely given. Selecting any of these pre-answers the DPIA screening's
 * vulnerable-data-subjects criterion, so the list is load-bearing rather than cosmetic.
 */
export const VULNERABLE_SUBJECT_CATEGORIES = new Set<string>([
  'children', 'patients', 'employees', 'job-applicants', 'students',
]);

// ─── Recipients — Article 30(1)(d) ────────────────────────────────────────────

export const RECIPIENT_CATEGORIES = [
  'internal-departments', 'group-companies', 'processors', 'sub-processors',
  'public-authorities', 'law-enforcement', 'professional-advisers',
  'financial-institutions', 'auditors', 'other',
] as const;

export const RECIPIENT_CATEGORY_LABELS: Record<string, string> = {
  'internal-departments':   'Internal departments',
  'group-companies':        'Other Mercell group companies',
  'processors':             'Processors',
  'sub-processors':         'Sub-processors',
  'public-authorities':     'Public authorities',
  'law-enforcement':        'Law enforcement',
  'professional-advisers':  'Professional advisers',
  'financial-institutions': 'Financial institutions',
  'auditors':               'Auditors',
  'other':                  'Other',
};

// ─── Scale ────────────────────────────────────────────────────────────────────
// Banded rather than an exact count: the number is never known precisely, and what the
// assessment actually turns on is whether processing is "large scale" (Art 35(3)(b), EDPB
// criterion 5). A band is honest and still answers that.

export const SUBJECT_VOLUME_BANDS = [
  '', 'under-100', '100-1000', '1000-10000', '10000-100000', '100000-1m', 'over-1m',
] as const;

export const SUBJECT_VOLUME_LABELS: Record<string, string> = {
  '':              'Not yet estimated',
  'under-100':     'Fewer than 100',
  '100-1000':      '100 – 1,000',
  '1000-10000':    '1,000 – 10,000',
  '10000-100000':  '10,000 – 100,000',
  '100000-1m':     '100,000 – 1 million',
  'over-1m':       'More than 1 million',
};

/** Bands at or above which the EDPB "large scale" criterion is treated as met. */
export const LARGE_SCALE_BANDS = new Set<string>(['10000-100000', '100000-1m', 'over-1m']);

// ─── Transfers — Article 30(1)(e) / Chapter V ─────────────────────────────────

export const TRANSFER_MECHANISMS: TransferMechanism[] = [
  'adequacy', 'sccs', 'bcrs', 'derogation', 'none',
];

export const TRANSFER_MECHANISM_LABELS: Record<TransferMechanism, string> = {
  'adequacy':   'Adequacy decision (Art 45)',
  'sccs':       'Standard contractual clauses (Art 46(2)(c))',
  'bcrs':       'Binding corporate rules (Art 47)',
  'derogation': 'Derogation for a specific situation (Art 49)',
  'none':       'No mechanism recorded',
};

export const TRANSFER_MECHANISM_TONE: Record<TransferMechanism, string> = {
  'adequacy':   'success',
  'sccs':       'success',
  'bcrs':       'success',
  'derogation': 'warning',
  // Deliberately loud. A transfer with no Chapter V basis is the most reportable state a
  // ROPA can be in, and it blocks activation server-side.
  'none':       'danger',
};

// ─── DPIA screening state, written back by the DPIA register ──────────────────

export const DPIA_VERDICT_LABELS: Record<DpiaVerdict, string> = {
  '':               'Not screened',
  'required':       'DPIA required',
  'not_required':   'DPIA not required',
  'discretionary':  'DPIA discretionary',
  // Screened, but an 'unknown' answer still blocks the verdict. Distinct from 'Not screened':
  // this one is somebody's unfinished work rather than nobody's.
  'incomplete':     'Screening incomplete',
};

export const DPIA_VERDICT_TONE: Record<DpiaVerdict, string> = {
  '':               'neutral',
  'required':       'danger',
  'not_required':   'success',
  'discretionary':  'warning',
  // Not 'neutral' — an unresolved screening needs to read as outstanding, not as untouched.
  'incomplete':     'info',
};

// ─── DPIA Part 4 — the 11 risk dimensions ──────────────────────────────────────
// Verbatim from the real DPIA template, and from _DPIA_RISK_DIMENSIONS in
// lambda/prod-nimbus-ai-use-cases/lambda_function.py (drafting) and the identical tuple in
// lambda/prod-nimbus-documents/lambda_function.py (the risk-dimensions PUT route's validation) -
// no shared layer for a list this small, so all three copies need updating together if it changes.
export const DPIA_RISK_DIMENSIONS = [
  'Purpose limitation', 'Data minimisation', 'Accuracy', 'Storage limitation',
  'Rights of the data subject', 'Lawful processing', 'Geographical scope', 'Accountability',
  'Integrity and confidentiality', 'Privacy by design and default', 'Freedoms',
] as const;

export type RiskScore = 'low' | 'medium' | 'high' | 'maximum';
export const RISK_SCORES: RiskScore[] = ['low', 'medium', 'high', 'maximum'];
export const RISK_SCORE_LABELS: Record<RiskScore, string> = {
  low: 'Low', medium: 'Medium', high: 'High', maximum: 'Maximum',
};
export const RISK_SCORE_TONE: Record<RiskScore, string> = {
  low: 'success', medium: 'warning', high: 'danger', maximum: 'danger',
};

export interface RiskDimensionEntry {
  dimension: string;
  riskScore: RiskScore;
  mitigation: string;
  residualRiskAcceptable: boolean;
  updatedAt?: number;
  updatedBy?: string;
}

// A new, separate rollup field from dpiaVerdict above - that answers "is a DPIA needed",
// decided before one exists; this answers "how much residual risk is left", which can only
// exist once Part 4 actually has data. '' = nothing scored yet, 'acceptable' = every scored
// dimension is marked residualRiskAcceptable, otherwise the worst unresolved riskScore.
export type ResidualRiskLevel = '' | 'acceptable' | RiskScore;
export const RESIDUAL_RISK_LABELS: Record<ResidualRiskLevel, string> = {
  '': 'Not yet assessed',
  acceptable: 'Residual risk accepted',
  low: 'Low residual risk',
  medium: 'Medium residual risk',
  high: 'High residual risk',
  maximum: 'Maximum residual risk',
};
export const RESIDUAL_RISK_TONE: Record<ResidualRiskLevel, string> = {
  '': 'neutral',
  acceptable: 'success',
  low: 'success',
  medium: 'warning',
  high: 'danger',
  maximum: 'danger',
};

// ─── Personal data type suggestions ────────────────────────────────────────────
// A `dataFields` row's `category`/`sensitivity`/`isPersonalData` are already closed
// vocabularies shared across every register that uses this shape (Processing Activities, AI
// Use Cases, Subprocessors) - `name` (the specific data type, e.g. "Email address") was the
// one part still fully free text. A hard enum would be wrong here: real personal-data types
// are open-ended, and a DPO who needs to record something not on the list shouldn't be
// blocked. This is a curated suggestion list, not a validated one - same shape as AI Use
// Cases' own DATA_SOURCE_SUGGESTIONS datalist, not a new UX pattern.
//
// Grouped so a form can default `isSpecialCategory: true` when a value from the "Special
// category" group is picked - the GDPR Article 9 list, spelled out as prose in
// `lambda/shared/screening/criteria_gdpr.py`, restructured here into discrete, selectable
// values for the first time.
export const PERSONAL_DATA_TYPE_GROUPS: { group: string; values: string[] }[] = [
  {
    group: 'Identifiers',
    values: [
      'Full name', 'Email address', 'Phone number', 'Postal address', 'Date of birth',
      'National ID / passport number', 'Username or login ID', 'IP address',
      'Device or browser identifier', 'Photograph',
    ],
  },
  {
    group: 'Financial',
    values: [
      'Bank account / IBAN', 'Payment card data', 'Salary / compensation data',
      'Tax identification number', 'Invoice or billing data',
    ],
  },
  {
    group: 'Employment / HR',
    values: [
      'Employment contract data', 'Performance review data', 'Disciplinary record',
      'CV / résumé', 'Emergency contact details',
    ],
  },
  {
    group: 'Special category (Article 9)',
    values: [
      'Health / medical record', 'Biometric identifier', 'Genetic data',
      'Racial or ethnic origin', 'Religious or philosophical belief', 'Political opinion',
      'Trade union membership', 'Sex life or sexual orientation', 'Criminal conviction data',
    ],
  },
  {
    group: 'Technical / behavioural',
    values: [
      'Location / GPS data', 'Usage or behavioural analytics', 'Cookie or tracking identifier',
      'Authentication token',
    ],
  },
  {
    group: 'Communications',
    values: ['Support ticket content', 'Chat or message content', 'Call recording'],
  },
];

export const PERSONAL_DATA_TYPE_SUGGESTIONS: string[] =
  PERSONAL_DATA_TYPE_GROUPS.flatMap((g) => g.values);

const SPECIAL_CATEGORY_DATA_TYPES = new Set<string>(
  PERSONAL_DATA_TYPE_GROUPS.find((g) => g.group === 'Special category (Article 9)')?.values ?? [],
);

/** Whether picking this data-type name should pre-check isSpecialCategory for the user. */
export const isSpecialCategoryDataType = (name: string): boolean =>
  SPECIAL_CATEGORY_DATA_TYPES.has(name.trim());

// ─── Helpers ──────────────────────────────────────────────────────────────────

/** True when the selected data-subject categories include a vulnerable group. */
export const hasVulnerableSubjects = (categories: string[]): boolean =>
  categories.some((c) => VULNERABLE_SUBJECT_CATEGORIES.has(c));

/** True when the recorded volume band meets the EDPB "large scale" threshold. */
export const isLargeScale = (band: string): boolean => LARGE_SCALE_BANDS.has(band);

/** Article 9 data present anywhere in the activity's data categories. */
export const hasSpecialCategoryData = (fields: { isSpecialCategory?: boolean }[]): boolean =>
  fields.some((f) => f.isSpecialCategory);

/** A transfer with no lawful Chapter V mechanism — blocks activation. */
export const hasUnlawfulTransfer = (transfers: { mechanism?: string }[]): boolean =>
  transfers.some((t) => t.mechanism === 'none');

/** Retention rules keyed by data category, for rendering alongside the data fields. */
export const retentionByCategory = (
  rules: { dataCategory: DataCategory; period: string }[],
): Partial<Record<DataCategory, string>> =>
  rules.reduce<Partial<Record<DataCategory, string>>>((acc, r) => {
    if (r.period) acc[r.dataCategory] = r.period;
    return acc;
  }, {});
